import React from 'react';
import { motion } from 'framer-motion';
import type { HeatmapData } from '../types';
import { HEATMAP_COLORS } from '../constants';

interface HeatmapGridProps {
    heatmaps: HeatmapData[];
    isLoading: boolean;
}

export const HeatmapGrid: React.FC<HeatmapGridProps> = ({ heatmaps, isLoading }) => {
    if (isLoading || heatmaps.length === 0) {
        return (
            <div className="mx-[18px] h-[120px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl animate-pulse" />
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-[18px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4"
        >
            <h3 className="text-[13px] font-medium text-[var(--text)] mb-4">Карта доступности</h3>
            
            <div className="space-y-4">
                {heatmaps.map((heatmap, hi) => (
                    <div key={heatmap.siteId}>
                        <div className="text-[11px] text-[var(--text-secondary)] mb-2">{heatmap.siteName}</div>
                        <div className="flex gap-[2px] flex-wrap">
                            {heatmap.cells.map((cell, ci) => (
                                <motion.div
                                    key={`${cell.date}-${cell.hour}`}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: hi * 0.1 + ci * 0.005 }}
                                    className="w-3 h-3 rounded-[2px] cursor-pointer relative group"
                                    style={{ background: HEATMAP_COLORS[cell.status].bg }}
                                    title={`${cell.date} ${cell.hour}:00 — ${cell.uptime.toFixed(1)}%`}
                                >
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                        <div className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded px-2 py-1 text-[10px] whitespace-nowrap shadow-lg">
                                            {cell.date} {String(cell.hour).padStart(2, '0')}:00<br/>
                                            Uptime: {cell.uptime.toFixed(1)}%
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="flex items-center gap-3 mt-4 text-[10px] text-[var(--text-muted)]">
                <span>Меньше</span>
                {(['excellent', 'good', 'warning', 'critical'] as const).map(status => (
                    <span key={status} className="flex items-center gap-1">
                        <span 
                            className="w-3 h-3 rounded-[2px]" 
                            style={{ background: HEATMAP_COLORS[status].bg }}
                        />
                        <span className="capitalize">{status === 'excellent' ? '100%' : status === 'good' ? '95%' : status === 'warning' ? '90%' : '<85%'}</span>
                    </span>
                ))}
                <span>Больше проблем</span>
            </div>
        </motion.div>
    );
};